/* eslint-disable no-param-reassign */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { sec } from './vulcan';
import { getFields } from '../Components/FormEditor/Editor/editorSlice';
import { buildSchema } from '../helpers/schema';
import { getActions } from '../Components/FormActions/formActionsSlice';
import { getForm } from '../Components/FormSetup/formSlice';

const baseQueryWithReauth = () => async (args, api, extraOptions) => {
  const account = sec.getAccount();
  const baseQuery = fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_APP_URL_API}/app`,
    prepareHeaders: (headers) => {
      if (account) {
        headers.set('X-Submize-account', account);
      }
      return headers;
    },
  });
  return baseQuery(args, api, extraOptions);
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth(),
  tagTypes: ['Form', 'Submission', 'OrgMembers', 'OrgMe'],
  endpoints: (builder) => ({
    getForms: builder.query({
      query: () => '/form/all',
      // eslint-disable-next-line default-param-last
      providesTags: () => ['Form'],
    }),
    getForm: builder.query({
      query: (id) => `/form/${id}`,
      providesTags: (result, error, arg) => [{ type: 'Form', id: arg }],
    }),
    getFormDraft: builder.query({
      query: (id) => `/form/${id}/draft`,
      providesTags: (result, error, arg) => [{ type: 'Form', id: arg, v: 'draft' }],
    }),
    getFormSubmissions: builder.query({
      query: ({ formId, next, search }) => ({
        url: `/form/${formId}/submissions`,
        params: { next, search },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        let key = `${endpointName}-${queryArgs.formId}`;
        if (queryArgs.search) {
          key += `-${queryArgs.search}`;
        }
        return key;
      },
      providesTags: (result, error, arg) => [{ type: 'Submission', id: arg.formId }],
      merge: (currentCache, newItems, ...others) => {
        console.log('others', others);
        currentCache.items.forEach((i) => { i.new = false; });
        currentCache.items.push(...newItems.items.map((i) => ({ ...i, new: true })));
        currentCache.next = newItems.next;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !previousArg || (currentArg.next !== '' && currentArg.next !== previousArg.next);
      },
    }),
    updateForm: builder.mutation({
      queryFn: async (_, api, extraOptions, baseQuery) => {
        const state = api.getState();
        const form = getForm(state);
        const fields = getFields(state);
        const actions = getActions(state);
        const newForm = buildSchema(form, fields, actions);

        return baseQuery({
          url: `/form/${form.id}`,
          method: 'POST',
          body: newForm,
        });
      },
      invalidatesTags: (result, error, form) => {
        if (error) return [];
        return [{ type: 'Form', id: form.id, v: 'draft' }];
      },
    }),
    publishForm: builder.mutation({
      query: (id) => ({
        url: `/form/${id}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, form) => {
        if (error) return [];
        return [{ type: 'Form', id: form.id }];
      },
    }),
    discardDraft: builder.mutation({
      query: (id) => ({
        url: `/form/${id}/draft/discard`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, form) => {
        if (error) return [];
        return [{ type: 'Form', id: form.id, v: 'draft' }];
      },
    }),
    readSubmition: builder.mutation({
      query: (data) => ({
        url: `/form/${data.formId}/${data.submitionId}/read`,
        method: 'POST',
        body: data.submition,
      }),
      onQueryStarted({ formId, submitionId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData('getFormSubmissions', { formId }, (draft) => {
            const s = draft.items.find((i) => i.id === submitionId);
            s.read = true;
          }),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    newForm: builder.mutation({
      query: () => ({
        url: '/form/new',
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newForm } = await queryFulfilled;
          if (newForm.status === 'ok') {
            dispatch(
              apiSlice.util.updateQueryData('getForms', undefined, (draft) => {
                draft.push(newForm.form);
              }),
            );
          }
        } catch (e) {
          console.log('error al crear el form', e);
        }
      },
    }),
    deleteForm: builder.mutation({
      query: (id) => ({
        url: `/form/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.status === 'ok') {
            dispatch(
              apiSlice.util.updateQueryData('getForms', undefined, (draft) => draft.filter((f) => f.id !== id)),
            );
          }
        } catch (e) {
          console.log('error al borrar el form', e);
        }
      },
      invalidatesTags: (result, error, id) => [{ type: 'Form', id }, { type: 'Submission', id }],
    }),
    enableForm: builder.mutation({
      query: (id) => ({
        url: `/form/${id}/enable`,
        method: 'POST',
      }),
      onQueryStarted(formId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData('getForms', undefined, (draft) => {
            const s = draft.find((i) => i.id === formId);
            s.enabled = true;
          }),
        );
        queryFulfilled.catch(patchResult.undo);
        const patchResult2 = dispatch(
          apiSlice.util.updateQueryData('getForm', formId, (draft) => {
            draft.enabled = true;
          }),
        );
        queryFulfilled.catch(patchResult2.undo);
      },
    }),
    disableForm: builder.mutation({
      query: (id) => ({
        url: `/form/${id}/disable`,
        method: 'POST',
      }),
      onQueryStarted(formId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiSlice.util.updateQueryData('getForms', undefined, (draft) => {
            const s = draft.find((i) => i.id === formId);
            s.enabled = false;
          }),
        );
        queryFulfilled.catch(patchResult.undo);
        const patchResult2 = dispatch(
          apiSlice.util.updateQueryData('getForm', formId, (draft) => {
            draft.enabled = false;
          }),
        );
        queryFulfilled.catch(patchResult2.undo);
      },
    }),
    getFormStats: builder.query({
      query: () => '/form/stats',
    }),
  }),
});

export const {
  useGetFormQuery,
  useGetFormsQuery,
  useGetFormSubmissionsQuery,
  useLazyGetFormSubmissionsQuery,
  useUpdateFormMutation,
  useReadSubmitionMutation,
  useNewFormMutation,
  useDeleteFormMutation,
  useEnableFormMutation,
  useDisableFormMutation,
  useGetFormStatsQuery,
  useGetFormDraftQuery,
  usePublishFormMutation,
  useDiscardDraftMutation,
} = apiSlice;
